import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { resetCompanyState } from '../../store/companySlice';
import store from '../../store/indexRedux';

// Helper function to check if token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const { exp } = JSON.parse(jsonPayload);
    const expirationTime = exp * 1000; // exp is in seconds, so multiply by 1000 for milliseconds
    // console.log(expirationTime + ' exp time');
    // console.log(Date.now() + ' NOW');
    return Date.now() >= expirationTime;
  } catch (error) {
    console.error('Error parsing token for expiration', error);
    return true; // Consider the token expired if there's an error parsing it
  }
};

// Retrieve session from cookie set by login-app
export const getSessionFromCookie = (): CognitoUserSession | null => {
  const sessionStr = document.cookie.split('; ').find((row) => row.startsWith('session='));
  const googleTokenStr = document.cookie.split('; ').find((row) => row.startsWith('google_access_token='));

  if (googleTokenStr) {
    const googleToken = googleTokenStr.split('=')[1];

    // Check for Google token expiration using the expiry time stored in the cookie (not localStorage)
    const expiresInCookie = document.cookie.split('; ').find((row) => row.startsWith('google_token_expires_in='));
    const expiryTimestamp = expiresInCookie ? parseInt(expiresInCookie.split('=')[1], 10) : null;
    // console.log(expiryTimestamp + ' exp time google');
    // console.log(Date.now() + ' exp time now');

    if (expiryTimestamp && Date.now() >= expiryTimestamp) {
      // console.log('Google token expired, logging out.');
      logout(); // Google token expired, log out
      return null;
    }

    // Simulate a Cognito session object for Google login
    return {
      getIdToken: () => ({
        getJwtToken: () => googleToken,
        payload: { email: 'Google User' },
      }),
      getAccessToken: () => ({
        getJwtToken: () => googleToken,
      }),
      getRefreshToken: () => ({
        getToken: () => '',
      }),
    } as unknown as CognitoUserSession;
  }

  if (!sessionStr) {
    console.error('No session found in cookies');
    return null;
  }

  try {
    const sessionData = JSON.parse(sessionStr.split('=')[1]);
    const IdToken = new CognitoIdToken({ IdToken: sessionData.IdToken });
    const AccessToken = new CognitoAccessToken({ AccessToken: sessionData.AccessToken });
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: sessionData.RefreshToken });

    if (isTokenExpired(IdToken.getJwtToken())) {
      // console.log('Cognito token expired, logging out.');
      logout(); // Cognito token expired, log out
      return null;
    }

    return new CognitoUserSession({
      IdToken,
      AccessToken,
      RefreshToken,
    });
  } catch (e) {
    console.error('Failed to parse session from cookie', e);
    return null;
  }
};

const sendLogoutStatusToStatamic = async () => {
  try {
    await fetch('https://yuso.com/api/portal/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        loggedIn: false, // Indicate that the user is logging out
      }),
    });
  } catch (error) {
    console.error('Failed to send logout status to Statamic:', error);
  }
};

export const logout = async () => {
  store.dispatch(resetCompanyState());
  localStorage.removeItem('selectedCompanyId');
  localStorage.removeItem('selectedUserCompany');
    localStorage.clear(); 

  const isLocalhost = window.location.hostname === 'localhost';

  // Explicitly clear cookies across all subdomains under `yuso.io`
  const options = isLocalhost
    ? 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost'
    : 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.yuso.io;';

  document.cookie = `session=; ${options}`;
  document.cookie = `google_access_token=; ${options}`;
  document.cookie = `google_token_expires_in=; ${options}`;

  // Clear any session data from localStorage or sessionStorage, if necessary
  localStorage.removeItem('google_token_expires_in');
  sessionStorage.removeItem('referrer');

  try {
    // Notify Statamic about the logout
    await sendLogoutStatusToStatamic();
  } catch (error) {
    console.error('Failed to send logout status to Statamic:', error);
  }

  // Redirect to login page to complete logout process
  window.location.href = import.meta.env.VITE_LOGIN;
};
